/**
 * One hidden file input, shared by every slot on the page.
 *
 * Not one per slot: a 52-shot ACB would otherwise mount 52 file inputs, and
 * Safari holds a decoded preview for each one it has been given a photo.
 *
 * The imperative `open()` matters. Safari only honours `input.click()` inside
 * the user gesture that caused it, so the slot's `onClick` must reach the input
 * synchronously — routing through state and an effect loses the gesture and the
 * camera silently never opens.
 */

import { forwardRef, useImperativeHandle, useRef, type ChangeEvent } from 'react';
import { acceptPick, cameraAttributes, CameraError, type CaptureMode, type Picked } from '../capture/camera.js';
import { browserPipeline, type ImagePipeline } from '../capture/photo.js';
import type { ShotProfile } from '../domain/types.js';

export interface CameraHandle {
  /** Opens the camera or the library for this shot. Call it inside the tap handler. */
  open(profile: ShotProfile, mode?: CaptureMode): void;
}

export interface CameraInputProps {
  onPhoto: (profile: ShotProfile, picked: Picked) => void;
  /** Called when a pick could not be used. A cancelled picker is not reported. */
  onProblem?: ((profile: ShotProfile, message: string) => void) | undefined;
  pipeline?: ImagePipeline | undefined;
}

export const CameraInput = forwardRef<CameraHandle, CameraInputProps>(function CameraInput(
  { onPhoto, onProblem, pipeline },
  ref,
) {
  const inputRef = useRef<HTMLInputElement>(null);
  // Which shot the open picker belongs to. A ref, not state: it is read in the
  // change handler and must not cause a render between tap and camera.
  const pending = useRef<ShotProfile | null>(null);

  useImperativeHandle(ref, () => ({
    open(profile, mode = 'camera') {
      const el = inputRef.current;
      if (!el) return;
      pending.current = profile;
      const attrs = cameraAttributes(mode);
      el.accept = attrs.accept;
      if (attrs.capture) el.setAttribute('capture', attrs.capture);
      else el.removeAttribute('capture');
      // Clearing before rather than after: Safari fires no `change` when the
      // same file is chosen twice, so a retake of an identical shot would
      // silently do nothing.
      el.value = '';
      el.click();
    },
  }));

  async function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const profile = pending.current;
    const files = event.target.files;
    pending.current = null;
    if (!profile) return;

    try {
      const picked = await acceptPick(files, pipeline ?? browserPipeline);
      onPhoto(profile, picked);
    } catch (err) {
      // Backing out of the picker is not a failure and must not blank the slot.
      if (err instanceof CameraError && /no photo was chosen/.test(err.message)) return;
      onProblem?.(profile, err instanceof Error ? err.message : String(err));
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      capture="environment"
      // Hidden from sight but not from the accessibility tree's point of view
      // unreachable: the slot buttons are the control, this is the mechanism.
      className="camera-input"
      style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      tabIndex={-1}
      aria-hidden="true"
      data-testid="camera-input"
      onChange={handleChange}
    />
  );
});
